// Shared vocabulary for Response Actions. The commands shown here mirror what
// frontend/public/agent/agent.py (0.3.0) actually runs; keep them in sync.
import { parseTs } from '../SecurityAlerts/model';

export const STATUS = {
  pending: { l: 'Waiting for confirmation', mk: 'var(--sev-high-fg)', mkB: 'transparent', chip: 'var(--sev-high-bg)' },
  confirmed: { l: 'Sent · waiting for agent', mk: 'transparent', mkB: 'var(--ink-text)', chip: 'var(--surf-raised)' },
  dispatched: { l: 'Picked up · running', mk: 'var(--ink-muted)', mkB: 'transparent', chip: 'var(--surf-raised)' },
  completed: { l: 'Done', mk: 'var(--sev-good-fg)', mkB: 'transparent', chip: 'var(--sev-good-bg)' },
  failed: { l: 'Failed', mk: 'var(--sev-crit-fg)', mkB: 'transparent', chip: 'var(--sev-crit-bg)' },
  cancelled: { l: 'Cancelled', mk: 'var(--sev-low-fg)', mkB: 'transparent', chip: 'var(--sev-low-bg)' },
};

export const TYPE_LABEL = {
  block_ip: 'Block IP', unblock_ip: 'Remove block', block_domain: 'Block domain', unblock_domain: 'Remove domain block', kill_process: 'Stop process', quarantine_file: 'Quarantine file',
  restore_file: 'Restore file', isolate_host: 'Isolate host', release_isolation: 'Release isolation',
};
export const NEW_TYPES = [
  ['block_ip', 'Block an IP', 'Firewall rules on the host'],
  ['block_domain', 'Block a domain', 'Hosts-file entry on the host'],
  ['kill_process', 'Stop a process', 'SIGTERM to one PID'],
  ['quarantine_file', 'Quarantine a file', 'Move it out of reach'],
  ['isolate_host', 'Isolate the host', 'Cut it off the network'],
];
export const UNDO_LABEL = { block_ip: 'Remove block', block_domain: 'Remove block', quarantine_file: 'Restore file', isolate_host: 'Release isolation' };

// Same thresholds as Agent Registry: heartbeats come every 15 min.
const LATE_MIN = 30;
const SILENT_MIN = 60;

export const cfgOf = (a) => {
  if (!a?.action_config) return {};
  if (typeof a.action_config === 'object') return a.action_config;
  try { return JSON.parse(a.action_config) || {}; } catch { return {}; }
};
export const resultOf = (a) => {
  if (!a?.result) return null;
  try { return JSON.parse(a.result); } catch { return { message: String(a.result) }; }
};
const base = (p) => String(p || '').split(/[\\/]/).pop();

export const titleOf = (a) => {
  const c = cfgOf(a);
  const on = a.hostname ? ` on ${a.hostname}` : '';
  switch (a.action_type) {
    case 'block_ip': return `Block ${c.ip}${on}`;
    case 'unblock_ip': return `Remove block on ${c.ip}${on}`;
    case 'block_domain': return `Block ${c.domain}${on}`;
    case 'unblock_domain': return `Remove block on ${c.domain}${on}`;
    case 'kill_process': return `Stop ${c.expected_name || 'process'} (PID ${c.pid})${on}`;
    case 'quarantine_file': return `Quarantine ${base(c.path)}${on}`;
    case 'restore_file': return `Restore ${base(c.original_path)}${on}`;
    case 'isolate_host': return `Isolate ${a.hostname || 'host'}`;
    case 'release_isolation': return `Release isolation${on}`;
    default: return `${a.action_type}${on}`;
  }
};

export const isPlaybook = (a) => a.triggered_type === 'playbook';
export const runRef = (id) => `RUN-${String(id || '').slice(0, 8).toUpperCase()}`;
export const caseRef = (id) => String(id || '').toUpperCase();

export const minutesSince = (iso) => {
  const d = parseTs(iso);
  return d ? Math.max(0, (Date.now() - d.getTime()) / 60000) : null;
};
export const ago = (iso) => {
  const m = minutesSince(iso);
  if (m == null) return '';
  if (m < 1) return 'now';
  if (m < 60) return `${Math.floor(m)} min`;
  if (m < 1440) return `${Math.floor(m / 60)} h`;
  return `${Math.floor(m / 1440)} d`;
};
// "just now", "4 min ago", "2 h ago"
export const agoLong = (iso) => {
  const t = ago(iso);
  return !t ? '' : t === 'now' ? 'just now' : `${t} ago`;
};
export const stamp = (iso) => {
  const d = parseTs(iso);
  if (!d) return '';
  const hm = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return d.toDateString() === new Date().toDateString() ? hm : `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${hm}`;
};

export const agentBand = (lastSeen) => {
  const m = minutesSince(lastSeen);
  return m == null ? 'never' : m < LATE_MIN ? 'ok' : m < SILENT_MIN ? 'late' : 'silent';
};
export const agentLine = (a) => {
  if (a.host_deleted) return { t: `${a.hostname || 'This host'} has been deleted`, dot: 'transparent', ok: false };
  const band = agentBand(a.last_seen);
  const h = a.hostname || 'this host';
  if (band === 'ok') return { t: `Agent on ${h} reporting · last check-in ${agoLong(a.last_seen)}. It looks for actions every 30 s.`, dot: 'var(--sev-good-fg)', ok: true };
  if (band === 'late') return { t: `Agent on ${h} is late · last check-in ${agoLong(a.last_seen)}`, dot: 'var(--sev-med-fg)', ok: true };
  if (band === 'silent') return { t: `Agent on ${h} silent · last seen ${agoLong(a.last_seen)}. Nothing runs until it’s back.`, dot: 'var(--sev-high-fg)', ok: false };
  return { t: `No agent has checked in from ${h}. Nothing runs until one does.`, dot: 'transparent', ok: false };
};

const isWin = (a) => String(a.os_type || '').toLowerCase().startsWith('win');

// What happens, the exact commands, and a note, per action and OS.
export function explain(a) {
  const c = cfgOf(a);
  const win = isWin(a);
  const host = a.hostname || 'the host';
  const v6 = String(c.ip || '').includes(':');
  const ipt = v6 ? 'ip6tables' : 'iptables';
  const osNote = a.os_type ? '' : 'Host OS unknown; Linux commands shown.';
  switch (a.action_type) {
    case 'block_ip':
      return win ? {
        what: `The agent adds two Windows Firewall rules on ${host} so nothing goes to or comes from ${c.ip}.`,
        cmd: `netsh advfirewall firewall add rule name="DragonEye Block ${c.ip} in"  dir=in  action=block remoteip=${c.ip}\nnetsh advfirewall firewall add rule name="DragonEye Block ${c.ip} out" dir=out action=block remoteip=${c.ip}`,
        note: 'Windows host. A rule that already exists is reused, not added twice.',
      } : {
        what: `The agent adds two firewall rules on ${host} so nothing goes to or comes from ${c.ip}.`,
        cmd: `${ipt} -I INPUT  -s ${c.ip} -j DROP -m comment --comment dragoneye-block\n${ipt} -I OUTPUT -d ${c.ip} -j DROP -m comment --comment dragoneye-block`,
        note: [osNote, 'Each rule is checked with -C first, so running it twice adds nothing.'].filter(Boolean).join(' '),
      };
    case 'unblock_ip':
      return win ? {
        what: `The agent deletes the two DragonEye rules for ${c.ip} on ${host}.`,
        cmd: `netsh advfirewall firewall delete rule name="DragonEye Block ${c.ip} in"\nnetsh advfirewall firewall delete rule name="DragonEye Block ${c.ip} out"`,
        note: 'Windows host.',
      } : {
        what: `The agent deletes the DragonEye block rules for ${c.ip} on ${host}. Other rules are untouched.`,
        cmd: `${ipt} -D INPUT  -s ${c.ip} -j DROP -m comment --comment dragoneye-block\n${ipt} -D OUTPUT -d ${c.ip} -j DROP -m comment --comment dragoneye-block`,
        note: osNote,
      };
    case 'block_domain':
      return {
        what: `The agent adds ${c.domain} to the hosts file on ${host}, pointing it at 0.0.0.0, so the host can no longer reach it. Only that exact name is blocked, not its subdomains.`,
        cmd: win ? `echo 0.0.0.0 ${c.domain} # dragoneye-block >> %SystemRoot%\\System32\\drivers\\etc\\hosts\nipconfig /flushdns` : `echo "0.0.0.0 ${c.domain} # dragoneye-block" >> /etc/hosts\nresolvectl flush-caches`,
        note: [osNote, 'An entry that already exists is not added twice. Programs that use their own DNS-over-HTTPS can bypass the hosts file. Needs agent 0.5.0 or later.'].filter(Boolean).join(' '),
      };
    case 'unblock_domain':
      return {
        what: `The agent removes the DragonEye entries for ${c.domain} from the hosts file on ${host}. Other entries are untouched.`,
        cmd: win ? `(remove lines ending "# dragoneye-block" for ${c.domain} from hosts)\nipconfig /flushdns` : `sed -i '/ ${c.domain} # dragoneye-block$/d' /etc/hosts\nresolvectl flush-caches`,
        note: osNote,
      };
    case 'kill_process':
      return {
        what: `The agent first checks that PID ${c.pid} is still ${c.expected_name}. If it is, it sends a stop signal. If the PID now belongs to another program, it stops nothing and reports that.`,
        cmd: win ? `tasklist /FI "PID eq ${c.pid}"   # must be ${c.expected_name}\nos.kill(${c.pid}, SIGTERM)          # TerminateProcess on Windows` : `cat /proc/${c.pid}/comm   # must be ${c.expected_name}\nkill -TERM ${c.pid}`,
        note: win ? 'Windows ends the process immediately; it gets no chance to clean up.' : 'PIDs get reused after a process ends, which is why the name is checked.',
      };
    case 'quarantine_file':
      return {
        what: `The agent moves ${base(c.path)} into its quarantine folder on ${host} and records where, so it can be restored.`,
        cmd: win ? `move "${c.path}"\n  → %USERPROFILE%\\.dragoneye\\quarantine\\${base(c.path)}.<timestamp>` : `mv "${c.path}" \\\n  ~/.dragoneye/quarantine/${base(c.path)}.<timestamp>`,
        note: 'The home folder is the account the agent runs as.',
      };
    case 'restore_file':
      return {
        what: `The agent moves the file back to ${c.original_path}. It won’t overwrite anything already there.`,
        cmd: win ? `move "${c.quarantined_to}"\n  → "${c.original_path}"` : `mv "${c.quarantined_to}" \\\n  "${c.original_path}"`,
        note: 'Only files inside the quarantine folder can be restored.',
      };
    case 'isolate_host':
      return win ? {
        what: `The agent switches ${host}'s firewall to block all inbound and outbound traffic, except DNS and DragonEye, so it keeps reporting and can be released.`,
        cmd: 'netsh advfirewall firewall add rule name="DragonEye Isolation Worker" dir=out action=allow protocol=TCP remoteport=443 remoteip=<DragonEye IPs>\nnetsh advfirewall firewall add rule name="DragonEye Isolation DNS" dir=out action=allow protocol=UDP remoteport=53\nnetsh advfirewall set allprofiles firewallpolicy blockinbound,blockoutbound',
        note: 'Windows host. The current firewall policy is saved first and put back on release. DragonEye’s IPs are re-checked every 30 s.',
      } : {
        what: `The agent sends all outbound traffic from ${host} through its own chain, which allows only loopback, DNS and DragonEye. Open connections are cut too, so the agent keeps reporting and can receive the release.`,
        cmd: 'iptables -N DRAGONEYE_ISOLATE\niptables -A DRAGONEYE_ISOLATE -o lo -j ACCEPT\niptables -A DRAGONEYE_ISOLATE -p udp --dport 53 -j ACCEPT   # and tcp\niptables -A DRAGONEYE_ISOLATE -p tcp -d <DragonEye IPs> --dport 443 -j ACCEPT\niptables -A DRAGONEYE_ISOLATE -j DROP\niptables -I OUTPUT 1 -j DRAGONEYE_ISOLATE',
        note: [osNote, 'Same for ip6tables. The host’s own OUTPUT rules are left alone. DragonEye’s IPs are re-checked every 30 s.'].filter(Boolean).join(' '),
      };
    case 'release_isolation':
      return win ? {
        what: `The agent puts ${host}'s firewall policy back the way it was before isolation and removes the two allow rules.`,
        cmd: 'netsh advfirewall set <each profile> firewallpolicy <saved policy>\nnetsh advfirewall firewall delete rule name="DragonEye Isolation Worker"\nnetsh advfirewall firewall delete rule name="DragonEye Isolation DNS"',
        note: 'Windows host. With no saved policy it uses the Windows default: block inbound, allow outbound.',
      } : {
        what: `The agent removes its isolation chain from ${host}. Normal network access comes back.`,
        cmd: 'iptables -D OUTPUT -j DRAGONEYE_ISOLATE\niptables -F DRAGONEYE_ISOLATE\niptables -X DRAGONEYE_ISOLATE',
        note: [osNote, 'Same for ip6tables.'].filter(Boolean).join(' '),
      };
    default:
      return { what: '', cmd: '', note: '' };
  }
}

// A plain next step for common failures.
export function failureHint(a, res) {
  const err = String(res?.error || '');
  const h = a.hostname || 'the host';
  if (/0\.3\.0/.test(err)) return `Update the agent on ${h} (re-run its launcher from Agent Registry), then queue it again.`;
  if (/Permission denied/i.test(err)) return `The agent on ${h} isn’t running as root or Administrator, so it can’t do this. Restart it with sudo or as Administrator, then queue it again.`;
  if (/is now .*not /i.test(err)) return 'The PID now belongs to a different program, so nothing was stopped. Find the current PID and queue a new action.';
  if (/Nothing is running as PID|exited before/i.test(err)) return 'The process had already ended. Nothing to do unless it has started again under a new PID.';
  if (/No result from the agent/i.test(err)) return `Check ${h} by hand before queuing it again: it may already have run.`;
  if (/not found|gone/i.test(err)) return 'The file isn’t where the action expected it.';
  if (/resolve/i.test(err)) return `${h} couldn’t look up DragonEye, so it wasn’t isolated: isolating it would have cut the agent off.`;
  return '';
}
